import { configureStore, combineReducers } from "@reduxjs/toolkit";
import { RVTask } from "@regal-voice/shared-types";
import pick from "lodash/pick";
import { useDispatch } from "react-redux";
import {
    persistReducer,
    FLUSH,
    REHYDRATE,
    PAUSE,
    PERSIST,
    PURGE,
    REGISTER,
    createMigrate,
    PersistedState,
} from "redux-persist";
import storage from "redux-persist/lib/storage/session";

import { getLogger } from "Services/LoggingService";
import { hostRestApiMiddleware, hostRestApiReducerPaths, hostRestApiReducers } from "Services/restApi";
import userReducer from "Services/state/users/UserSlice";

import agentInformation from "./agent/AgentInformationSlice";
import agentDesktopUI from "./agent/desktopUI/AgentDesktopUISlice";
import emailUIPersist from "./agent/desktopUI/email/EmailUIPersistSlice";
import emailUI from "./agent/desktopUI/email/EmailUISlice";
import brand from "./brand/BrandProvider";
import brandedPhoneNumbers from "./branded-phone-numbers/IndexProvider";
import conferenceSnapshot from "./conferences/ConferenceSnapshotSlice";
import conference from "./conferences/IndexProvider";
import contactAttributes from "./contact-attributes";
import contacts from "./contacts/IndexProvider";
import conversations from "./conversations/IndexProvider";
import customObjects from "./custom-objects/IndexProvider";
import { globalMessageMiddleware } from "./global-messages/GlobalMessages";
import "./IndexedDBStorage"; // Ensure IndexedDBStorage is initialized
import inputPersist from "./input-persist/IndexProvider";
import IVRNodeData from "./ivr/IVRNodeDataStateSlice";
import journey from "./journeys/IndexProvider";
import globalLayout from "./layout/GlobalLayoutSlice";
import managerAction from "./manager-action/IndexProvider";
import savedViews from "./saved-views/SavedViewsProvider";
import tasks from "./tasks/IndexProvider";
import transcriptions from "./transcriptions/IndexProvider";

import type { AnyAction, Middleware } from "redux";

// ---- Helper types (Redux 4/5 compatible) ----
type AnyReduxAction = AnyAction | { type: unknown };

// --- session logout clear ---
const root = sessionStorage.getItem("persist:root") ? JSON.parse(sessionStorage.getItem("persist:root") || "") : {};
const userInformation = root?.userInformation;
const didManuallyLogOut = userInformation && JSON.parse(userInformation).didManuallyLogOut;
if (didManuallyLogOut) {
    storage.removeItem("persist:root");
}

// --- migrations ---
const persistMigrationHandlers = {
    0: (state: PersistedState) => {
        const {
            inputPersist: { activeContactPanelTab, taskSummaries, agentInput },
        } = state as unknown as RootState & {
            inputPersist: {
                agentInput: Record<string, string>;
                taskSummaries: Record<string, RVTask>;
                activeContactPanelTab: Record<string, RVTask>;
            };
        };
        return {
            ...state,
            inputPersist: {
                textMessageInput: agentInput,
                emailInput: {},
                taskSummaries,
                activeContactPanelTab,
            },
        } as PersistedState;
    },
    1: (state: PersistedState) => {
        const {
            inputPersist: {
                textMessageInput,
                emailInput,
                textMessageLexicalInput,
                emailLexicalInput,
                taskSummaries,
                activeContactPanelTab,
            },
        } = state as unknown as RootState;
        return {
            ...state,
            inputPersist: {
                textMessageInput,
                textMessageLexicalInput,
                emailInput,
                emailLexicalInput,
                taskSummaries,
                activeContactPanelTab,
                activityFeedConversationFilter: {},
            },
        } as PersistedState;
    },
    2: (state: PersistedState) => {
        const { inputPersist } = (state as unknown as RootState) ?? {};

        return {
            ...state,
            inputPersist: {
                ...(inputPersist ?? {}),
                // Added for Copilot draft persistence
                copilotLexicalInput: (inputPersist as any)?.copilotLexicalInput ?? {},
            },
        } as PersistedState;
    },
};

const persistConfig = {
    key: "root",
    // Session storage
    storage,
    blacklist: [
        ...hostRestApiReducerPaths,
        "brand",
        "conversations",
        "tasks",
        "contacts",
        "conference",
        "conferenceSnapshot",
        "journey",
        "IVRNodeData",
        "managerAction",
        "emailUI",
        "globalLayout",
    ],
    version: 2,
    migrate: createMigrate(persistMigrationHandlers),
};

const persistedConference = persistReducer(
    { key: "conference", storage, whitelist: ["connectionQuality"] },
    conference
);

export const reduxRootReducer = combineReducers({
    ...hostRestApiReducers,
    agentDesktopUI,
    agentInformation,
    brand,
    brandedPhoneNumbers,
    conference: persistedConference,
    conferenceSnapshot,
    contactAttributes,
    contacts,
    conversations,
    customObjects,
    emailUI,
    emailUIPersist,
    globalLayout,
    inputPersist,
    journey,
    IVRNodeData,
    managerAction,
    savedViews,
    tasks,
    transcriptions,
    userInformation: userReducer,
});

const persistedReducer = persistReducer(persistConfig, reduxRootReducer);

// --- logging middleware (state-agnostic, plays nice with PersistPartial) ---
const logger = getLogger("Redux");

const whitelist = [
    "brand",
    "agentInformation",
    "conversations",
    "conference",
    "conferenceSnapshot",
    "tasks",
    "inputPersist",
    "managerAction",
];

const reduxLogger: Middleware = (store) => (next) => (action: AnyReduxAction) => {
    const result = next(action);
    const type = (action as AnyAction)?.type ?? "unknown";
    const state = pick(store.getState(), whitelist);
    logger.debug(type, { action, state });
    return result;
};

// --- store ---
export const reduxStore = configureStore({
    reducer: persistedReducer,
    // @ts-expect-error Redux Toolkit Middleware type issue
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({
            immutableCheck: false,
            serializableCheck: {
                ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
                ignoredActionPaths: ["payload.conversationInstance"],
                ignoredPaths: ["conversations.activeConversations.instances"],
            },
        }).concat(...hostRestApiMiddleware, reduxLogger, globalMessageMiddleware as Middleware),
    devTools: { name: "Regal AI Store" },
});

// --- typed hooks & helpers ---
export type RVStore = typeof reduxStore;
export type RVDispatch = typeof reduxStore.dispatch;
export const useRVDispatch: () => RVDispatch = useDispatch;

export type AsyncThunkStore = { state: RootState; dispatch: RVDispatch };
export type ThunkType = (dispatch: RVDispatch, getState: () => RootState) => void;
